import React, { useState, useEffect, useRef } from "react";
import "./NewRequestsPage.css";
import api from '../../api';
import { 
  FaFilePdf, 
  FaFileWord, 
  FaFileExcel, 
  FaFilePowerpoint, 
  FaFileVideo, 
  FaFileAudio, 
  FaFileAlt, 
  FaFileArchive, 
  FaCode, 
  FaFile,
  FaImage,
  FaTimes
} from 'react-icons/fa';

export default function NewRequestsPage() {
  const [taskTypes, setTaskTypes] = useState([]);
  const [priorities, setPriorities] = useState([]);
  const [people, setPeople] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    type: "",
    priority: "",
    requester: "",
    email: "",
  });
  const [matchedPerson, setMatchedPerson] = useState(null);
  const [isNewUser, setIsNewUser] = useState(false);

  // Fetch dropdown options on mount
  useEffect(() => {
    Promise.all([
      api.get('/tasks/types'),
      api.get('/tasks/priorities'),
      api.get('/tasks/people')
    ])
      .then(([typesRes, prioritiesRes, peopleRes]) => {
        setTaskTypes(typesRes.data || []);
        setPriorities(prioritiesRes.data || []);
        setPeople(peopleRes.data || []);
        
        // Don't set defaults - let user select
      })
      .catch((err) => {
        console.error("Failed to fetch dropdown options:", err);
      });
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'email') {
      // Check if email matches a person in the database
      const person = people.find(p => p.emailAddress.toLowerCase() === value.toLowerCase());
      
      if (person && value.trim() !== '') {
        // Email matches - show their name (read-only)
        setMatchedPerson(person);
        setIsNewUser(false);
        setFormData(prev => ({ 
          ...prev, 
          email: value,
          requester: person.name 
        }));
      } else if (value.trim() === '') {
        // Email field is empty - reset
        setMatchedPerson(null);
        setIsNewUser(false);
        setFormData(prev => ({ 
          ...prev, 
          email: value,
          requester: '' 
        }));
      } else {
        // Email doesn't match - new user
        setMatchedPerson(null);
        setIsNewUser(true);
        setFormData(prev => ({ 
          ...prev, 
          email: value,
          requester: '' 
        }));
      }
    } else {
      // For other fields, just update normally
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    // Add new files to selected files
    const newFiles = files.map(file => ({
      file,
      id: Date.now() + Math.random(), // Temporary ID
      name: file.name,
      size: file.size,
      type: file.type
    }));
    
    setSelectedFiles(prev => [...prev, ...newFiles]);
    
    // Reset input to allow selecting same file again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemoveFile = (fileId) => {
    setSelectedFiles(prev => prev.filter(f => f.id !== fileId));
  };

  const handleUploadButtonClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const uploadFilesToTask = async (taskId) => {
    if (selectedFiles.length === 0) return;

    setUploadingFiles(true);
    try {
      const uploadPromises = selectedFiles.map(async (fileItem) => {
        // Upload to blob storage
        const formData = new FormData();
        formData.append('file', fileItem.file);
        
        const uploadRes = await api.post('/images/upload', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });

        // Associate file with task
        const fileRes = await api.post('/task-images', {
          task_id: taskId,
          file_url: uploadRes.data.fileUrl || uploadRes.data.imageUrl,
          image_url: uploadRes.data.fileUrl || uploadRes.data.imageUrl, // Backward compatibility
          file_name: uploadRes.data.fileName,
          original_file_name: uploadRes.data.originalFileName || uploadRes.data.fileName,
          file_size: uploadRes.data.fileSize,
          mime_type: uploadRes.data.mimeType,
        });

        return fileRes.data;
      });

      await Promise.all(uploadPromises);
    } catch (err) {
      console.error('Failed to upload files:', err);
      throw err; // Re-throw to handle in handleSubmit
    } finally {
      setUploadingFiles(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const getFileIcon = (mimeType, fileName) => {
    if (mimeType?.startsWith('image/')) return <FaImage />;
    if (mimeType?.startsWith('video/')) return <FaFileVideo />;
    if (mimeType?.startsWith('audio/')) return <FaFileAudio />;
    
    const ext = fileName?.split('.').pop()?.toLowerCase();
    if (ext === 'pdf') return <FaFilePdf />;
    if (['doc', 'docx'].includes(ext)) return <FaFileWord />;
    if (['xls', 'xlsx'].includes(ext)) return <FaFileExcel />;
    if (['ppt', 'pptx'].includes(ext)) return <FaFilePowerpoint />;
    if (['zip', 'rar', '7z', 'tar', 'gz'].includes(ext)) return <FaFileArchive />;
    if (['js', 'ts', 'jsx', 'tsx', 'html', 'css', 'json', 'xml'].includes(ext)) return <FaCode />;
    return <FaFileAlt />;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitSuccess(false);

    try {
      const payload = {
        title: formData.title,
        description: formData.description,
        type: formData.type || null,
        priority: formData.priority || null,
        status: "New", // Default status for requests from this form
        personName: formData.requester,
        personEmail: formData.email,
      };

      const res = await api.post('/tasks', payload);
      const taskId = res.data.id;
      
      // Upload files if any were selected
      if (selectedFiles.length > 0 && taskId) {
        await uploadFilesToTask(taskId);
      }
      
      // Reset form
      setFormData({
        title: "",
        description: "",
        type: "",
        priority: "",
        requester: "",
        email: "",
      });
      setMatchedPerson(null);
      setIsNewUser(false);
      setSelectedFiles([]);

      setSubmitSuccess(true);
      setTimeout(() => setSubmitSuccess(false), 3000);
    } catch (err) {
      console.error("Failed to create task:", err);
      alert('Failed to create task. Please try again.');
    } finally {
      setIsSubmitting(false);
      setUploadingFiles(false);
    }
  };

  return (
    <section className="new-request-section">
      <h2>New Requests</h2>

      <form className="request-form" onSubmit={handleSubmit}>
        <div className="form-field">
          <label htmlFor="title">Title</label>
          <input
            type="text"
            id="title"
            name="title"
            placeholder="Task title"
            value={formData.title}
            onChange={handleChange}
            required
          />
        </div>

        <div className="form-field">
          <label htmlFor="description">Description</label>
          <textarea
            id="description"
            name="description"
            placeholder="Task description"
            value={formData.description}
            onChange={handleChange}
            rows="3"
          />
        </div>

        <div className="form-row">
          <div className="form-field">
            <label htmlFor="type">Type</label>
            <select
              id="type"
              name="type"
              value={formData.type || ""}
              onChange={handleChange}
              required
            >
              <option value="">Select type</option>
              {taskTypes.map((type) => (
                <option key={type.id} value={type.type}>
                  {type.type}
                </option>
              ))}
            </select>
          </div>

          <div className="form-field">
            <label htmlFor="priority">Priority</label>
            <select
              id="priority"
              name="priority"
              value={formData.priority || ""}
              onChange={handleChange}
              required
            >
              <option value="">Select priority</option>
              {priorities.map((priority) => (
                <option key={priority.id} value={priority.priorityLevel}>
                  {priority.priorityLevel}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="form-row">
          <div className="form-field">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              name="email"
              placeholder="Enter your email address"
              value={formData.email}
              onChange={handleChange}
              required
            />
            {matchedPerson && (
              <div className="email-match-message">
                ✓ Email found: {matchedPerson.name}
              </div>
            )}
            {isNewUser && formData.email.trim() !== '' && (
              <div className="new-user-message">
                ⚠ New user - enter name
              </div>
            )}
          </div>

          <div className="form-field">
            <label htmlFor="requester">Requester Name</label>
            {matchedPerson ? (
              <input
                type="text"
                id="requester"
                name="requester"
                value={formData.requester}
                readOnly
                className="requester-readonly"
              />
            ) : (
              <input
                type="text"
                id="requester"
                name="requester"
                placeholder={isNewUser ? "Enter your name" : "Enter email first"}
                value={formData.requester}
                onChange={handleChange}
                required={isNewUser}
                disabled={!isNewUser && formData.email.trim() === ''}
              />
            )}
          </div>
        </div>

        <div className="form-field">
          <label htmlFor="attachments">Attachments</label>
          <input
            ref={fileInputRef}
            type="file"
            id="attachments"
            multiple
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />
          <button
            type="button"
            onClick={handleUploadButtonClick}
            className="file-select-btn"
            disabled={isSubmitting || uploadingFiles}
          >
            {uploadingFiles ? 'Uploading...' : '+ Add Files'}
          </button>
          
          {selectedFiles.length > 0 && (
            <div className="selected-files-list">
              {selectedFiles.map((fileItem) => (
                <div key={fileItem.id} className="selected-file-item">
                  <span className="file-icon">{getFileIcon(fileItem.type, fileItem.name)}</span>
                  <span className="file-name" title={fileItem.name}>{fileItem.name}</span>
                  <span className="file-size">{formatFileSize(fileItem.size)}</span>
                  <button
                    type="button"
                    onClick={() => handleRemoveFile(fileItem.id)}
                    className="remove-file-btn"
                    disabled={isSubmitting || uploadingFiles}
                    title="Remove file"
                    aria-label="Remove file"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <button type="submit" className="submit-btn" disabled={isSubmitting || uploadingFiles}>
          {isSubmitting || uploadingFiles ? 'Submitting...' : 'Submit Request'}
        </button>

        {submitSuccess && (
          <div className="submit-success">
            ✅ Request submitted successfully!
          </div>
        )}
      </form>
    </section>
  );
}
